import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';
// Import shared components
import { H3, ListingLink } from '../../../../components';

// Import modules from this directory
import css from './EditListingTalentPlusPanel.module.css';
import { createSlug } from '../../../../util/urlHelpers';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import EditListingTalentPlusForm from './EditListingTalentPlusForm';
import {
  getProfileVideos,
  getProfileVideoURL,
  getTalentPlus,
  getUnlockableContent,
} from '../../../../util/listingDataExtraction';
import { filterSubscriptionsByAuthor, isArrayLength } from '../../../../util/genericHelpers';
import { showErrorToast } from '../../../../util/toast';
import { uploadVideoFiles } from '../../../../util/mediaHelpers';
import { createResourceLocatorString } from '../../../../util/routes';
import EditListingLayoutSideNav from '../EditListingLayoutSideNav/EditListingLayoutSideNav';
import { mapFilteredServices } from '../EditListingMoreInfoPanel/EditListingMoreInfoPanel';
import { useSelector } from 'react-redux';
import { userSelector } from '../../../../ducks/user.duck';
import {
  getSubscriptionPriceId,
  getUserSubscriptionLookupkeys,
  getUserType,
} from '../../../../util/userDataExtraction';
import { subscriptionPlanSelector } from '../../../../ducks/stripe.duck';
import { DIRECT_MESSAGE } from '../../../../util/constants';

const getInitialValues = listing => {
  const videos = getProfileVideos(listing); // Retrieves the profile videos
  const videoUrls = (isArrayLength(videos) && [...(getProfileVideoURL(videos) || [])]) || []; // Extracts the URLs from the videos
  const talentPlus = getTalentPlus(listing);
  const unlockableContent = getUnlockableContent(listing);

  return {
    profileVideos: videoUrls,
    talentPlus,
    subscriptionType: 'Recurring',
    unlockableContent,
  };
};

const EditListingTalentPlusPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    config,
    isTalentPlus,
    subscriptionPrices,
  } = props;
  const existingProfileVideos = getProfileVideos(listing);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const listingId = listing?.id;
  const listingTitle = listing?.attributes?.title;
  const slug = createSlug(listingTitle);
  const classes = classNames(rootClassName || css.root, className);
  const initialValues = getInitialValues(listing);
  const isPublished = listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;

  const currentUser = useSelector(userSelector);
  const currentUserId = currentUser?.id?.uuid;
  const subscriptionMaybe =
    isArrayLength(subscriptionPrices) &&
    filterSubscriptionsByAuthor(subscriptionPrices, currentUserId);
  const lookupKeys = getUserSubscriptionLookupkeys(currentUser);
  const userType = getUserType(currentUser);

  const handleSubmit = async (values, form, shouldSkipNextTab) => {
    setUploadInProgress(true);

    try {
      const { profileVideos = [], talentPlus = [], unlockableContent = {} } = values;
      const videosToUpload = profileVideos.filter(video => video.path);

      const uploadResponse = isArrayLength(videosToUpload)
        ? await uploadVideoFiles(videosToUpload, 'profileVideos', form, currentUserId)
        : [];
      const updatedProfileVideos = [...uploadResponse];
      const isDirectMessage =
        isArrayLength(talentPlus) &&
        talentPlus?.some(item => item?.selected === true && item?.type === DIRECT_MESSAGE);
      const unlockableTypes = [
        ...(isArrayLength(unlockableContent.photos) ? ['Photos'] : []),
        ...(isArrayLength(unlockableContent.posts) ? ['Posts'] : []),
        ...(isArrayLength(unlockableContent.videos) ? ['Videos'] : []),
        ...(isDirectMessage ? ['Direct Message'] : []),
      ];
      const updatedValues = {
        publicData: {
          profileVideos: updatedProfileVideos,
          talentPlus: mapFilteredServices(talentPlus),
          unlockableContent,
          isTalentPlusServices: true,
          ...(isArrayLength(unlockableTypes) && { unlockableTypes }),
        },
      };

      onSubmit(updatedValues);

      if (!shouldSkipNextTab) {
        setTimeout(() => {
          history.push(
            createResourceLocatorString('ListingPage', routeConfiguration, {
              id: listingId?.uuid,
              slug,
            })
          );
        }, 1000);
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      setUploadInProgress(false);
    }
  };

  return (
    <div className={classes}>
      <div className={css.contentLayoutWrapper}>
        <EditListingLayoutSideNav listing={listing} />
        <div className={css.detailContent}>
          <H3 as="h1">
            {isPublished ? (
              <FormattedMessage
                id="EditListingTalentPlusPanel.title"
                values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
              />
            ) : (
              <FormattedMessage
                id="EditListingTalentPlusPanel.createListingTitle"
                values={{ lineBreak: <br /> }}
              />
            )}
          </H3>

          <EditListingTalentPlusForm
            className={css.form}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            updateInProgress={updateInProgress}
            saveActionMsg={submitButtonText}
            disabled={disabled || uploadInProgress}
            ready={ready}
            updated={panelUpdated}
            userType={userType}
            subscriptionMaybe={subscriptionMaybe}
            lookupKeys={lookupKeys}
            onLiveSubmit={(values, form) => handleSubmit(values, form, true)}
          />
        </div>
      </div>
    </div>
  );
};

const { func, object, string, bool } = PropTypes;

EditListingTalentPlusPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingTalentPlusPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingTalentPlusPanel;
